import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Typography,
  useTheme,
  CircularProgress,
  Radio,
  Checkbox,
  IconButton,
  Tooltip,
  Switch,
  TextField
} from "@mui/material";
import { ArrowUpward } from "@mui/icons-material";
import { toLocal } from "../../utils/date";
import { parseFormattedNumber } from "../../utils/numberFormatter.js";
import FullScreenLoading from "../../component/FullScreenLoading";
const switchStyle = {
  width: 38,
  height: 14,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: 0,
  },
  "& .MuiSwitch-thumb": {
    width: "12px",
    height: "12px",
    mt: "1px",
    ml: "3px",
  },
  "& .Mui-checked": {
    color: "#fff!important",
  },
  "& .Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#00A3E0 !important",
    opacity: "1 !important",
  },
};

const TableComponent = ({
  columns,
  rows,
  fixedHeader = true,
  sortable = false,
  sortableColumns = [],
  selectable = "none",
  showRadioInSingleSelect = false,
  iconColumns = [], //iconColumns
  iconColumnName = null,
  emptyText = "No Data",
  headerStyles = {},
  iconStyles = { padding: 0 },
  iconImage = null,
  rowClickColor = "transparent",
  onHistoryUploadSuccess,
  columnWidths = {},
  selected = [],
  setSelected = () => { },
  onSwitchChange = () => { },
  showPagination = false,
  page = 0,
  rowsPerPage = 10,
  onRowsPerPageChange = () => { },
  onPageChange = () => { },
  isLoading,
  updateMode, //eidt -false
  editableColumns, //edit column
  onCellChange, //edit
  ...props
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState(null);
  const [isLoadingOperation, setIsLoadingOperation] = useState(false);
  const [orderMap, setOrderMap] = useState({});

  const handleRequestSort = (property) => {
    if (!sortable || !sortableColumns.includes(property)) return;
    setOrderBy(property);
    setOrderMap(prev => {
      const columnConfig = columns.find(c => c.id === property);
      const defaultOrder = columnConfig?.defaultSortOrder ?? "desc";
      let newOrder;
      if (prev[property] !== undefined) {
        newOrder = prev[property] === "desc" ? "asc" : "desc";
      } else {
        newOrder = defaultOrder === "desc" ? "asc" : "desc";
      }
      return {
        ...prev,
        [property]: newOrder,
      };
    });
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  const handleSelectAllClick = (event) => {
    if (selectable === "single") return;
    if (event.target.checked) {
      const newSelecteds = rows?.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    if (selectable === "single") {
      setSelected([id]);
    } else {
      const selectedIndex = selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onRowsPerPageChange(newRowsPerPage);
    onPageChange(0);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  };

  const getComparator = (order, orderBy, columnType) => {
    const isDesc = order === "desc";

    return (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (columnType === "number") {
        const cleanValue = (val) => {
          if (typeof val !== "string") return val;
          return val.replace(/,/g, "").replace(/[^\d.-]/g, "");
        };
        aValue = Number(cleanValue(aValue));
        bValue = Number(cleanValue(bValue));
        if (isNaN(aValue)) aValue = -Infinity;
        if (isNaN(bValue)) bValue = -Infinity;
      } else if (columnType === "date" || columnType === "dateWithTime") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        if (isNaN(aValue.getTime())) aValue = new Date(-Infinity); // Handle invalid dates
        if (isNaN(bValue.getTime())) bValue = new Date(-Infinity);
      } else { // Default to string comparison
        aValue = String(aValue);
        bValue = String(bValue);
      }

      if (aValue < bValue) return isDesc ? 1 : -1;
      if (aValue > bValue) return isDesc ? -1 : 1;
      return 0;
    };
  };

  const handleIconClick = (row, customAction, event) => {
    if (customAction) {
      customAction(row, event);
    } else {
      downloadFile(row, event);
    }
  };

  const downloadFile = (item, event) => {
    if (event) {
      event.stopPropagation();
    }
    setIsLoadingOperation(true);
    const link = document.createElement("a");
    link.href = item?.fileURL;
    link.download = item.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      onHistoryUploadSuccess && onHistoryUploadSuccess();
      setIsLoadingOperation(false);
    }, 3000);
  };

  useEffect(() => {
    if (sortable && sortableColumns.length > 0 && !orderBy) {
      const initialColumnId = sortableColumns[0];
      const columnConfig = columns.find(c => c.id === initialColumnId);
      const defaultOrder = columnConfig?.defaultSortOrder ?? "desc";
      setOrderBy(initialColumnId);
      setOrderMap({
        [initialColumnId]: defaultOrder,
      });
    }
  }, [sortable, sortableColumns, columns, orderBy]);

  const columnConfig = columns.find(c => c.id === orderBy);
  const columnType = columnConfig?.type ?? "string";

  const getCurrentOrder = () => {
    return orderMap[orderBy] ?? columns.find(c => c.id === orderBy)?.defaultSortOrder ?? "desc";
  };

  const sortedRows = useMemo(() => {
    // if (updateMode) return [...rows];// editing cell can't sort
    if (!orderBy) return rows;
    const comparator = getComparator(getCurrentOrder(), orderBy, columnType);
    return stableSort(rows, comparator);
  }, [rows, orderBy, orderMap, updateMode]);

  const displayedRows = showPagination
    ? sortedRows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", ...props.paperSx }}>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 440,
            boxShadow: showPagination
              ? "none"
              : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
            "& .MuiTableHead-root": {
              position: "sticky",
              top: 0,
              zIndex: 100,
            },
            "& .MuiTableCell-root": {
              paddingX: 1.5,
              paddingY: 0.5,
              fontSize: theme.typography.fontSize,
              background: "none",
              color: "unset",
            },
            ...props.sx,
          }}
        >
          <Table
            stickyHeader={fixedHeader}
            size="small"
            sx={{ height: displayedRows?.length === 0 ? "100%" : "auto" }}
          >
            <TableHead style={headerStyles["default"]}>
              <TableRow>
                {(selectable === "multiple" ||
                  (selectable === "single" && showRadioInSingleSelect)) && (
                    <TableCell
                      padding="checkbox"
                      width={columnWidths["checkbox"]}
                    >
                      {selectable === "multiple" && (
                        <Checkbox
                          indeterminate={
                            selected.length > 0 && selected.length < rows.length
                          }
                          checked={
                            rows.length > 0 && selected.length === rows.length
                          }
                          onChange={handleSelectAllClick}
                        />
                      )}
                    </TableCell>
                  )}
                {columns?.map((column, index) => {
                  const isActive = orderBy === column.id;
                  const currentOrder =
                    orderMap[column.id] ?? column.defaultSortOrder ?? "desc";
                  return (
                    <TableCell
                      key={`${column.id}-${index}`}
                      align={column.align}
                      sortDirection={isActive ? currentOrder : false}
                      style={{
                        ...headerStyles[column.id],
                        width: columnWidths[column.id],
                      }}
                    >
                      {sortable &&
                        sortableColumns.includes(column.id) &&
                        iconColumnName !== column.id ? (
                        <TableSortLabel
                          active={isActive}
                          direction={currentOrder}
                          onClick={createSortHandler(column.id)}
                          IconComponent={(props) => (
                            <ArrowUpward
                              {...props}
                              style={{
                                ...props.style,
                                opacity: isActive ? 1 : 0.3,
                                fontSize: "16px",
                              }}
                            />
                          )}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize:
                                headerStyles["default"]?.fontSize || "11px",
                            }}
                          >
                            {column.label}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  )
                })}
                {iconColumns.map((iconColumn, idx) => (
                  <TableCell
                    key={`icon-column-header-${idx}`}
                    align="center"
                    style={{
                      ...headerStyles[`iconColumn-${idx}`],
                      width: iconColumn.width,
                    }}
                  >
                    {iconColumn.label || ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={columns.length + iconColumns.length}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedRows?.length === 0 ? (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={
                      columns.length +
                      iconColumns.length +
                      (selectable === "multiple" ||
                        (selectable === "single" && showRadioInSingleSelect)
                        ? 1
                        : 0)
                    }
                    sx={{
                      fontSize: "22px!important",
                      color: "#BFBFBF!important",
                    }}
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                displayedRows?.map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const rowStyle =
                    rowClickColor && isItemSelected
                      ? { backgroundColor: rowClickColor }
                      : {};
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={`${row.id}-${index}`}
                      selected={isItemSelected}
                      style={rowStyle}
                    >
                      {(selectable === "multiple" ||
                        (selectable === "single" &&
                          showRadioInSingleSelect)) && (
                          <TableCell
                            sx={{
                              p: "0 6px!important",
                              ".MuiRadio-root": { p: 0 },
                            }}
                            width={columnWidths["checkbox"]}
                          >
                            {selectable === "multiple" ? (
                              <Checkbox checked={isItemSelected} />
                            ) : showRadioInSingleSelect ? (
                              <Radio checked={isItemSelected} />
                            ) : null}
                          </TableCell>
                        )}
                      {/* {columns?.map((column, i) => (
                        <TableCell
                          key={`${column.id}-${i}`}
                          align={column.align}
                          sx={{
                            textAlign: column.align,
                            paddingX: `${column.id === iconColumn ? 4 : 12
                              }px !important`,
                          }}
                          width={columnWidths[column.id]}
                        >
                          {column.id === iconColumn ? (
                            <IconButton
                              style={iconStyles}
                              onClick={() => {
                                if (!props.disableIconButtonClick)
                                  downloadFile(row);
                              }}
                            >
                              {typeof iconImage === "string" ? (
                                <img src={iconImage} alt="Icon" />
                              ) : typeof iconImage === "function" ? (
                                <img
                                  src={iconImage(row[column.id])}
                                  alt="Icon"
                                />
                              ) : (
                                <ArrowUpward />
                              )}
                            </IconButton>
                          ) : column.type === "switch" ? (
                            <Switch
                              checked={row[column.id]}
                              onChange={(event) =>
                                onSwitchChange(
                                  row.id,
                                  column.id,
                                  event.target.checked
                                )
                              }
                              sx={switchStyle}
                            />
                          ) : column.type === "date" ? (
                            row[column.id] != null ? (
                              toLocal(row[column.id]).format("DD/MM/YYYY")
                            ) : (
                              "--"
                            )
                          ) : column.type === "dateWithTime" ? (
                            row[column.id] != null ? (
                              toLocal(row[column.id]).format(
                                "DD/MM/YYYY - HH:mm"
                              )
                            ) : (
                              "--"
                            )
                          ) : row[column.id] != null &&
                            row[column.id] !== "null null" ? (
                            row[column.id]
                          ) : (
                            "--"
                          )}
                        </TableCell>
                      ))} */}
                      {columns?.map((column, i) => {
                        const cellValue = row[column.id];
                        const isEditable = updateMode && editableColumns.includes(column.id);
                        return (
                          <TableCell
                            key={`${column.id}-${i}`}
                            align={column.align}
                            sx={{
                              textAlign: column.align,
                              paddingX: `${column.id === iconColumnName ? 4 : 12}px !important`,
                            }}
                            width={columnWidths[column.id]}
                          >
                            {column.id === iconColumnName ? (
                              <IconButton
                                style={iconStyles}
                                onClick={() => {
                                  if (!props.disableIconButtonClick) downloadFile(row);
                                }}
                              >
                                {typeof iconImage === "string" ? (
                                  <img src={iconImage} alt="Icon" />
                                ) : typeof iconImage === "function" ? (
                                  <img src={iconImage(row[column.id])} alt="Icon" />
                                ) : (
                                  <ArrowUpward />
                                )}
                              </IconButton>
                            ) : isEditable ? (
                              <TextField
                                size="small"
                                value={parseFormattedNumber(cellValue)}
                                type="number"
                                sx={{ "& .MuiInputBase-input": { px: 1, py: 0.2 } }}
                                onChange={(e) => {
                                  if (onCellChange) {
                                    onCellChange(row.id, column.id, e.target.value);
                                  }
                                }}
                              />
                            ) : column.type === "switch" ? (
                              <Switch
                                checked={row[column.id]}
                                onChange={(event) =>
                                  onSwitchChange(
                                    row.id,
                                    column.id,
                                    event.target.checked
                                  )
                                }
                                sx={switchStyle}
                              />
                            ) : column.type === "date" ? (
                              row[column.id] != null ? (
                                toLocal(row[column.id]).format("DD/MM/YYYY")
                              ) : (
                                "--"
                              )
                            ) : column.type === "dateWithTime" ? (
                              row[column.id] != null ? (
                                toLocal(row[column.id]).format("DD/MM/YYYY - HH:mm")
                              ) : (
                                "--"
                              )
                            ) : cellValue != null && cellValue !== "null null" ? (
                              cellValue
                            ) : (
                              "--"
                            )}
                          </TableCell>
                        );
                      })}
                      {iconColumns.map((iconColumnConfig, idx) => {
                        const shouldDisplayIcon = iconColumnConfig.shouldDisplay
                          ? iconColumnConfig.shouldDisplay(row)
                          : true;
                        return (
                          <TableCell
                            key={`icon-column-cell-${idx}`}
                            align="center"
                            sx={{ textAlign: "center", p: "0!important" }}
                            width={columnWidths[`iconColumn-${idx}`]}
                          >
                            {shouldDisplayIcon ? (
                              <IconButton
                                style={iconStyles}
                                onClick={(event) =>
                                  handleIconClick(
                                    row,
                                    iconColumnConfig.action,
                                    event
                                  )
                                }
                              >
                                {iconColumnConfig.image ? (
                                  <Tooltip title={iconColumnConfig.note}>
                                    <img
                                      src={iconColumnConfig.image}
                                      alt="Icon"
                                      style={{ height: 20 }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <ArrowUpward />
                                )}
                              </IconButton>
                            ) : (
                              <span style={{ visibility: "hidden" }}>.</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <TablePagination
            component="div"
            count={rows?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage ?? 10}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "30", value: 30 },
              { label: "40", value: 40 },
              { label: "50", value: 50 },
            ]}
          />
        )}
      </Paper>
      <FullScreenLoading isLoading={isLoadingOperation} />
    </>
  );
};

export default TableComponent;
